import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { BookOpen, Target, Users, TrendingUp, Star, Trophy, BarChart3, Calendar } from 'lucide-react';
import { dashboardService } from '../../services/dashboardService';
import { authService } from '../../services/authService';
import { useToast } from '../../hooks/use-toast';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stats, setStats] = useState({});
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    checkAuthAndFetchData();
  }, []);

  const checkAuthAndFetchData = async () => {
    try {
      // Check if user is authenticated
      if (!authService.isAuthenticated()) {
        navigate('/admin/login');
        return;
      }

      // Get user data
      const userData = authService.getStoredUser();
      setUser(userData);
      setIsAuthenticated(true);

      // Fetch dashboard stats
      const statsData = await dashboardService.getDashboardStats();
      setStats(statsData);

    } catch (error) {
      console.error('Error loading dashboard:', error);
      
      // If auth error, redirect to login
      if (error.response?.status === 401) {
        authService.logout();
        navigate('/admin/login');
      } else {
        toast({
          title: "Error",
          description: "Failed to load dashboard data. Please refresh the page.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout isAdmin={true}>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </Layout>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Layout isAdmin={true}>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              Admin Dashboard
            </h1>
            <p className="text-lg text-gray-600">
              Welcome back, {user?.name || 'Admin'}! Here's what's happening with your book quiz platform.
            </p>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0 hover:shadow-xl transition-all hover:scale-105">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Books</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.total_books || 0}</p>
                    <p className="text-sm text-green-600 flex items-center mt-1">
                      <TrendingUp className="h-4 w-4 mr-1" />
                      Active
                    </p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-full">
                    <BookOpen className="h-8 w-8 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0 hover:shadow-xl transition-all hover:scale-105">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Quizzes</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.total_quizzes || 0}</p>
                    <p className="text-sm text-green-600 flex items-center mt-1">
                      <TrendingUp className="h-4 w-4 mr-1" />
                      Available
                    </p>
                  </div>
                  <div className="p-3 bg-purple-100 rounded-full">
                    <Target className="h-8 w-8 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0 hover:shadow-xl transition-all hover:scale-105">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Attempts</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.total_attempts || 0}</p>
                    <p className="text-sm text-green-600 flex items-center mt-1">
                      <TrendingUp className="h-4 w-4 mr-1" />
                      Quiz taken
                    </p>
                  </div>
                  <div className="p-3 bg-yellow-100 rounded-full">
                    <Trophy className="h-8 w-8 text-yellow-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0 hover:shadow-xl transition-all hover:scale-105">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Average Score</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.average_score || 0}%</p>
                    <p className="text-sm text-green-600 flex items-center mt-1">
                      <TrendingUp className="h-4 w-4 mr-1" />
                      Performance
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-full">
                    <Star className="h-8 w-8 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Popular Books */}
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2 text-blue-600" />
                  Popular Books
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {stats.popular_books && stats.popular_books.length > 0 ? (
                  stats.popular_books.map((book, index) => (
                    <div key={book.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                          index === 0 ? 'bg-yellow-500' :
                          index === 1 ? 'bg-gray-400' :
                          'bg-orange-500'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="ml-3">
                          <p className="font-medium text-gray-900">{book.title}</p>
                          <p className="text-sm text-gray-600">{book.total_attempts} attempts • {book.total_quizzes} quizzes</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="w-24">
                          <Progress value={stats.total_attempts > 0 ? (book.total_attempts / stats.total_attempts) * 100 : 0} className="h-2" />
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{book.average_score}% avg</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600">No books available yet</p>
                    <p className="text-sm text-gray-500">Add some books to see statistics</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Calendar className="h-5 w-5 mr-2 text-purple-600" />
                  System Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="p-2 bg-blue-100 rounded-full mr-3">
                      <BookOpen className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Books Management</p>
                      <p className="text-sm text-gray-600">{stats.total_books || 0} books available in the system</p>
                      <p className="text-xs text-gray-500 mt-1">Ready for quiz creation</p>
                    </div>
                  </div>

                  <div className="flex items-start p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="p-2 bg-green-100 rounded-full mr-3">
                      <Target className="h-4 w-4 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Quiz System</p>
                      <p className="text-sm text-gray-600">{stats.total_quizzes || 0} quizzes with {stats.total_questions || 0} questions</p>
                      <p className="text-xs text-gray-500 mt-1">Interactive learning ready</p>
                    </div>
                  </div>

                  <div className="flex items-start p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="p-2 bg-purple-100 rounded-full mr-3">
                      <Users className="h-4 w-4 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">User Management</p>
                      <p className="text-sm text-gray-600">{stats.total_users || 0} registered admin users</p>
                      <p className="text-xs text-gray-500 mt-1">Access control active</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0">
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div 
                  onClick={() => navigate('/admin/books')}
                  className="p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all cursor-pointer group"
                >
                  <BookOpen className="h-8 w-8 text-gray-400 group-hover:text-blue-600 mb-3" />
                  <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 mb-2">Manage Books</h3>
                  <p className="text-sm text-gray-600">Add, edit, or import books from CSV</p>
                </div>

                <div 
                  onClick={() => navigate('/admin/quizzes')}
                  className="p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-all cursor-pointer group"
                >
                  <Target className="h-8 w-8 text-gray-400 group-hover:text-purple-600 mb-3" />
                  <h3 className="font-semibold text-gray-900 group-hover:text-purple-600 mb-2">Manage Quizzes</h3>
                  <p className="text-sm text-gray-600">Create quizzes and import questions</p>
                </div>

                <div 
                  onClick={() => navigate('/admin/users')}
                  className="p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-400 hover:bg-green-50 transition-all cursor-pointer group"
                >
                  <Users className="h-8 w-8 text-gray-400 group-hover:text-green-600 mb-3" />
                  <h3 className="font-semibold text-gray-900 group-hover:text-green-600 mb-2">Manage Users</h3>
                  <p className="text-sm text-gray-600">Add admin users and manage access</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default AdminDashboard;